import {
  computed,
  nextTick,
  onMounted,
  readonly,
  ref,
  watch,
  type Ref,
} from "vue";

export type ChangePayload = { value: Date | null; event: any };

type PopupAction = "now" | "set" | null;
type AnyArgsHandler = (...args: unknown[]) => void;

export function useMaskedTimeInput(args: {
  id: string;
  onChange: (p: ChangePayload) => void;
  onShowPicker: (e: KeyboardEvent) => void;
  externalValue?: Ref<string | Date | null | undefined>;
  // Minute stepping granularity for ArrowUp/ArrowDown when focused in minutes.
  // Supported values: 1, 5, 10, 15, 20, 30. If not provided, defaults to 1-minute steps.
  minuteStep?: 1 | 5 | 10 | 15 | 20 | 30;
  // Reactive alternative: pass a Ref to allow dynamic updates at runtime.
  minuteStepRef?: Ref<1 | 5 | 10 | 15 | 20 | 30 | undefined>;
  timeFormat?: string;
  min?: Date | null | undefined;
  max?: Date | null | undefined;
  /** When true, empty/incomplete value is considered invalid after blur */
  required?: boolean;
  debug?: boolean;
}) {
  const raw = ref("");
  const cursorPos = ref<number | undefined>(undefined);
  const debugEnabled = ref<boolean>(!!args.debug);
  const placeholder = ref<string>(args.timeFormat ?? "hh:mm AM");
  const isRequired = computed(() => !!args.required);
  const isTouched = ref(false);

  const steppers: string[] = ["ArrowUp", "ArrowDown"];
  const cursorKeys: string[] = ["ArrowLeft", "ArrowRight"];

  const digitsOnly = computed(() => (raw.value ?? "").replace(/\D/g, ""));

  // Mask rules to be used by MaskedTextBox
  // Relax numeric constraints: allow any digits, then validate afterwards
  const rules = {
    H: /[0-9]/,
    h: /[0-9]/,
    M: /[0-9]/,
    m: /[0-9]/,
    A: /[AaPp]/,
    a: /[Mm]/,
  } as const;

  const partAt = (pos: number) => {
    if (pos <= 2) return "hh"; // hours
    if (pos <= 5) return "mm"; // minutes
    return "ampm"; // AM/PM
  };

  const hour = computed(() => {
    const h = raw.value
      ? raw.value.substring(0, 2).replace(/\D/g, "0") || "0"
      : "0";
    const v = parseInt(h);
    return Math.min(Math.max(v, 0), 23); // keep as 0-23 for parsing convenience
  });
  const minute = computed(() => {
    const m = raw.value
      ? raw.value.substring(3, 5).replace(/\D/g, "0") || "0"
      : "0";
    const v = parseInt(m);
    return Math.min(Math.max(v, 0), 59);
  });
  const period = computed(() => {
    const p = (raw.value?.substring(6, 8) || "AM").toUpperCase();
    return p.startsWith("P") ? "PM" : "AM";
  });

  const hour24 = computed(() => {
    const h12 = parseInt(raw.value?.substring(0, 2).replace(/\D/g, "0") || "0");
    const p = period.value;

    // Convert 12-hour to 24-hour format
    if (h12 === 12) {
      return p === "AM" ? 0 : 12;
    }
    return p === "PM" ? h12 + 12 : h12;
  });

  const time24 = computed(() => {
    if (!isComplete.value) return null;
    return `${String(hour24.value).padStart(2, "0")}:${String(minute.value).padStart(2, "0")}:00`;
  });

  const isComplete = computed(() =>
    /^(\d{2}):(\d{2})\s([AP]M)$/i.test(raw.value ?? ""),
  );

  const toDisplay = (h24: number, m: number, ap: "AM" | "PM") => {
    // display uses 12-hour formatting in the field
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    return `${String(h12).padStart(2, "0")}:${String(m).padStart(
      2,
      "0",
    )} ${ap}`;
  };

  const toDateFromDisplay = (display: string) => {
    const match = display.match(/^(\d{2}):(\d{2})\s([AP]M)$/i);
    if (!match) return null;
    const hh = parseInt(match[1]);
    const mm = parseInt(match[2]);
    const ap = match[3].toUpperCase();
    // Enforce 12-hour bounds and minute bounds; reject invalid formats like 99:99 AM
    if (hh < 1 || hh > 12 || mm < 0 || mm > 59) return null;
    let h24 = hh % 12;
    if (ap === "PM") h24 += 12;
    const d = new Date();
    d.setSeconds(0, 0);
    d.setHours(h24);
    d.setMinutes(mm);
    return d;
  };

  const emitParsedIfComplete = (event: any) => {
    if (digitsOnly.value.length === 0) {
      args.onChange({ value: null, event });
      return;
    }
    if (!isComplete.value) {
      args.onChange({ value: null, event });
      return;
    }
    const date = toDateFromDisplay(raw.value);
    if (date && inRangeTime(date)) {
      args.onChange({ value: date, event });
    } else {
      args.onChange({ value: null, event });
    }
  };
  const minutesSinceMidnight = (d: Date) => d.getHours() * 60 + d.getMinutes();
  const inRangeTime = (d: Date) => {
    const min = args.min ?? undefined;
    const max = args.max ?? undefined;
    const m = minutesSinceMidnight(d);
    if (min) {
      const minM = minutesSinceMidnight(min);
      if (m < minM) return false;
    }
    if (max) {
      const maxM = minutesSinceMidnight(max);
      if (m > maxM) return false;
    }
    return true;
  };

  const syncRawFromDate = (date: Date) => {
    const h24 = date.getHours();
    const ap = h24 >= 12 ? "PM" : "AM";
    raw.value = toDisplay(h24, date.getMinutes(), ap);
  };

  const emitValue = (value: Date | null, event: any) => {
    args.onChange({ value, event });
  };

  const focusInputAfterPopupClose = () => {
    // Defer focus long enough for the popup close lifecycle to complete.
    nextTick(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const inputEl = document.getElementById(
            args.id,
          ) as HTMLInputElement | null;
          if (inputEl) {
            inputEl.focus();
            const end = (inputEl.value ?? "").length;
            inputEl.setSelectionRange(end, end);
          }
        });
      });
    });
  };

  const inferPopupAction = (event: any): PopupAction => {
    const target = event?.event?.target as HTMLElement | null | undefined;
    if (!target) return null;
    if (target.closest(".k-time-now")) return "now";
    if (target.closest(".k-time-accept")) return "set";
    return null;
  };

  const emitParsedDate = (date: Date | null, event: any) => {
    if (date && inRangeTime(date)) {
      emitValue(date, event);
    } else {
      emitValue(null, event);
    }
  };

  const handleChange: AnyArgsHandler = (...eventArgs) => {
    const event = eventArgs[0] as any;
    if (!event) return;

    const action = inferPopupAction(event);

    // TimePicker popup path: distinguish NOW vs SET and normalize value handling.
    if (action === "now") {
      const now = new Date();
      syncRawFromDate(now);
      emitParsedDate(now, event);
      focusInputAfterPopupClose();
      return;
    }

    if (event?.value instanceof Date) {
      const nextValue = new Date(event.value);
      syncRawFromDate(nextValue);
      emitParsedDate(nextValue, event);
      if (action === "set") {
        focusInputAfterPopupClose();
      }
      return;
    }

    if (event?.value === null) {
      raw.value = "";
      emitValue(null, event);
      return;
    }

    // Set raw first from event
    raw.value = event.value as string;
    const src = event.event?.target as HTMLInputElement;
    cursorPos.value = src?.selectionStart ?? 0;
    // If caret is in AM/PM segment and user typed A/a or P/p, auto-complete to AM/PM
    try {
      const pos = cursorPos.value ?? 0;
      if (partAt(pos) === "ampm" && (raw.value ?? "").length >= 8) {
        const first = raw.value.charAt(6);
        if (/[Aa]/.test(first)) {
          raw.value = `${raw.value.slice(0, 6)}AM${raw.value.slice(8)}`;
          // Move caret to end of AM/PM
          nextTick(() => {
            requestAnimationFrame(() => {
              const inputEl = document.getElementById(
                args.id,
              ) as HTMLInputElement | null;
              if (inputEl) {
                inputEl.focus();
                inputEl.setSelectionRange(8, 8);
              }
            });
          });
        } else if (/[Pp]/.test(first)) {
          raw.value = `${raw.value.slice(0, 6)}PM${raw.value.slice(8)}`;
          nextTick(() => {
            requestAnimationFrame(() => {
              const inputEl = document.getElementById(
                args.id,
              ) as HTMLInputElement | null;
              if (inputEl) {
                inputEl.focus();
                inputEl.setSelectionRange(8, 8);
              }
            });
          });
        }
      }
    } catch {}
    emitParsedIfComplete(event);
  };

  const handleClick = (event: MouseEvent): void => {
    cursorPos.value = (event.target as HTMLInputElement).selectionStart ?? 0;
  };

  const applyStep = (
    direction: "ArrowUp" | "ArrowDown",
    sourceEl: HTMLInputElement | null | undefined,
    event: Event,
  ): void => {
    const pos = sourceEl?.selectionStart ?? cursorPos.value ?? 0;
    cursorPos.value = pos;
    const part = partAt(pos);

    const dispMatch = (raw.value ?? "").match(/^(\d{2}):(\d{2})\s([AP]M)$/i);
    if (!dispMatch) {
      // If empty, initialize to current time on first step
      if ((raw.value ?? "").length === 0 || digitsOnly.value.length === 0) {
        const now = new Date();
        const h24 = now.getHours();
        const ap = h24 >= 12 ? "PM" : "AM";
        raw.value = toDisplay(h24, now.getMinutes(), ap);
        const date = toDateFromDisplay(raw.value);
        emitParsedDate(date, event);
        nextTick(() => {
          requestAnimationFrame(() => {
            const inputEl = document.getElementById(
              args.id,
            ) as HTMLInputElement | null;
            const p = cursorPos.value ?? 0;
            if (inputEl) {
              inputEl.focus();
              inputEl.setSelectionRange(p, p);
            }
          });
        });
      }
      // For non-empty but invalid formats, do not auto-correct
      return;
    }
    let hh = parseInt(dispMatch[1]);
    let mm = parseInt(dispMatch[2]);
    let ap = dispMatch[3].toUpperCase() as "AM" | "PM";

    if (part === "hh") {
      if (direction === "ArrowUp") hh = hh < 12 ? hh + 1 : 1;
      else hh = hh > 1 ? hh - 1 : 12;
    } else if (part === "mm") {
      const step = args.minuteStepRef?.value ?? args.minuteStep ?? 1;
      if (step === 1) {
        if (direction === "ArrowUp") mm = mm < 59 ? mm + 1 : 0;
        else mm = mm > 0 ? mm - 1 : 59;
      } else {
        if (direction === "ArrowUp") {
          const rem = mm % step;
          let next = rem === 0 ? mm + step : mm + (step - rem);
          if (next >= 60) next = 0; // wrap to top of hour
          mm = next;
        } else {
          const rem = mm % step;
          let prev = rem === 0 ? mm - step : mm - rem;
          if (prev < 0) prev = 60 - step; // wrap to last step in hour
          mm = prev;
        }
      }
    } else if (part === "ampm") {
      ap = ap === "AM" ? "PM" : "AM";
    }

    raw.value = `${String(hh).padStart(2, "0")}:${String(mm).padStart(
      2,
      "0",
    )} ${ap}`;
    const date = toDateFromDisplay(raw.value);
    emitParsedDate(date, event);

    nextTick(() => {
      requestAnimationFrame(() => {
        const inputEl = document.getElementById(
          args.id,
        ) as HTMLInputElement | null;
        const p = cursorPos.value ?? 0;
        if (inputEl) {
          inputEl.focus();
          inputEl.setSelectionRange(p, p);
        }
      });
    });
  };

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.code === "Space" || event.key === " ") {
      // Open the picker without modifying the input
      // Justified: Space opens the time picker popup instead of inserting a space.
      event.preventDefault();
      // Justified: Parent containers such as grid keyboard handlers should not consume this.
      event.stopPropagation();
      args.onShowPicker(event);
      return;
    }

    // Alt+Down opens the picker; allow other Alt+arrow combinations to pass through
    if (event.key === "ArrowDown" && event.altKey) {
      // Justified: Alt+Down is reserved for opening the time picker popup.
      event.preventDefault();
      // Justified: Prevent parent keyboard navigation from treating this as row movement.
      event.stopPropagation();
      args.onShowPicker(event);
      return;
    }

    if (steppers.includes(event.key)) {
      if (event.altKey) return;
      // Justified: ArrowUp/Down act as time-part steppers instead of native caret/page behavior.
      event.preventDefault();
      // Justified: Prevent parent containers such as the grid from also handling these arrows.
      event.stopPropagation();
      const src = event.target as HTMLInputElement;
      applyStep(event.key as "ArrowUp" | "ArrowDown", src, event);
      return;
    }
  };

  const handleWheel = (event: WheelEvent): void => {
    // Map mouse wheel to stepper behavior (up/down) based on caret segment
    // Prevent page scroll when interacting with the input
    event.preventDefault();
    const direction = event.deltaY < 0 ? "ArrowUp" : "ArrowDown";
    applyStep(direction, event.target as HTMLInputElement, event);
  };

  const handleKeyUp = (event: KeyboardEvent): void => {
    const src = event.target as HTMLInputElement;
    if (cursorKeys.includes(event.key)) {
      // Do not prevent default for caret navigation
      cursorPos.value = src?.selectionStart ?? 0;
      return;
    }
    if (steppers.includes(event.key)) {
      if (event.altKey) return;
      cursorPos.value = src?.selectionStart ?? 0;
    }
  };

  const handleBlur = (event: FocusEvent): void => {
    isTouched.value = true;

    // If required and value is still undefined/blank, coerce external
    // value from undefined -> "" so downstream validation can treat
    // this as an incomplete required field.
    if (
      isRequired.value &&
      args.externalValue &&
      args.externalValue.value === undefined &&
      digitsOnly.value.length === 0
    ) {
      (args.externalValue as Ref<any>).value = "";
    }

    const src = event.target as HTMLInputElement | null;
    if (src) {
      cursorPos.value = src.selectionStart ?? 0;
    }
  };

  // Sync external value into raw when provided
  if (args.externalValue) {
    watch(
      args.externalValue,
      (newVal) => {
        if (newVal) {
          const date = new Date(newVal as any);
          if (date.toString() !== "Invalid Date") {
            const h24 = date.getHours();
            const ap = h24 >= 12 ? "PM" : "AM";
            raw.value = toDisplay(h24, date.getMinutes(), ap);
            return;
          }
        }
        // If external value is null/invalid, keep current raw as-is
      },
      { immediate: true },
    );
  }

  const debugLines = computed(() => {
    return [
      ["Time", raw.value || "--:-- --"],
      ["Hour (12h)", hour.value],
      ["Hour (24h)", hour24.value],
      ["Minute", minute.value],
      ["Period", period.value],
      ["Time (24h)", time24.value ?? "--:--:--"],
      ["Digits only", digitsOnly.value],
      [
        "Parsed Date",
        parsedRawTime.value?.toLocaleDateString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }) ?? null,
      ],
      [
        "min",
        args.min?.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }) ?? null,
      ],
      [
        "max",
        args.max?.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }) ?? null,
      ],
      ["valid", isValid.value],
      ["reason", reason.value],
      ["validationMessage", validationMessage.value],
    ];
  });

  // Validation based on completeness and parse-ability
  const parsedRawTime = computed<Date | null>(
    () => toDateFromDisplay(raw.value) ?? null,
  );
  const isValid = computed(() => {
    // Optional field preserves forgiving behavior
    if (!isRequired.value) {
      if (!isComplete.value) return true;
      const t = parsedRawTime.value;
      if (!t) return false;
      return inRangeTime(t);
    }

    // Required field: do not flag invalid until after blur
    if (!isTouched.value) return true;

    const t = parsedRawTime.value;
    if (!isComplete.value) return false;
    if (!t) return false;
    return inRangeTime(t);
  });

  const reason = computed<string | undefined>(() => {
    // Optional: only report reasons for completed-but-bad values
    if (!isRequired.value) {
      if (!isComplete.value) return undefined;
      const t = parsedRawTime.value;
      if (!t) return "invalid-time";
      if (!inRangeTime(t)) return "out-of-bounds";
      return "valid";
    }

    // Required: only validate after blur
    if (!isTouched.value) return undefined;

    if (digitsOnly.value.length === 0 || !isComplete.value) {
      return "incomplete";
    }

    const t = parsedRawTime.value;
    if (!t) return "invalid-time";
    if (!inRangeTime(t)) return "out-of-bounds";
    return "valid";
  });

  const validationMessage = computed(() => {
    const r = reason.value;
    if (!r || r === "valid") return "";

    if (r === "incomplete") {
      return "Required";
    }

    if (r === "invalid-time") {
      return `Must be in ${placeholder.value} format.`;
    }

    const opts: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
    };
    const minStr = args.min ? args.min.toLocaleTimeString("en-US", opts) : null;
    const maxStr = args.max ? args.max.toLocaleTimeString("en-US", opts) : null;
    if (r === "out-of-bounds") {
      if (minStr && maxStr) return `Must be between ${minStr} and ${maxStr}.`;
      if (minStr) return `Must be ${minStr} or later.`;
      if (maxStr) return `Must be ${maxStr} or earlier.`;
    }

    return "";
  });

  const initStyling = () => {
    // Idempotent application of the `fk-timepicker` class to the time picker's parent
    nextTick(() => {
      const inputEl = document.getElementById(args.id) as HTMLElement | null;
      if (!inputEl) {
        console.warn(`#${args.id} not found for styling.`);
        return;
      }
      const timePicker = inputEl.closest(".k-timepicker");
      if (!timePicker) {
        console.warn(
          `.k-timepicker parent of #${args.id} not found for styling.`,
        );
        return;
      }
      const parent = timePicker.parentElement;
      if (!parent) {
        console.warn(
          `Parent of .k-timepicker not found for #${args.id} styling.`,
        );
        return;
      }
      if (parent.classList.contains("fk-timepicker")) return;
      parent.classList.add("fk-timepicker");
    });
  };

  onMounted(() => {
    initStyling();
  });

  return {
    raw,
    rules,
    debugEnabled,
    debugLines,
    placeholder,
    isValid,
    reason: readonly(reason),
    validationMessage,
    isComplete,
    inRangeTime,
    cursorPos,
    hour: readonly(hour),
    hour24: readonly(hour24),
    minute: readonly(minute),
    period: readonly(period),
    time24: readonly(time24),
    parsedRawTime: readonly(parsedRawTime),
    handleChange,
    handleKeyDown,
    handleKeyUp,
    handleClick,
    handleWheel,
    handleBlur,
    // Expose so consumers can reapply theming when Kendo updates DOM
    initStyling,
  };
}
